/*  eslint-disable react/prop-types */
/* eslint-disable react/no-multi-comp */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Alert, Empty, Checkbox } from '@app/ui';
import { Masonry } from 'masonic';
import { useFormikContext } from 'formik';
import classNames from '@app/ui/sports-components/utils/classNames';
import { type FormValues } from './ReportsContext';
import './ReportCoverPicker.scss';

const COLUMN_WIDTH = 150;
const GUTTER_SIZE = 12;

class MasonryErrorBoundary extends React.Component<{
    children: React.ReactNode;
    errorMessage: string;
}> {
    static getDerivedStateFromError() {
        return { hasError: true };
    }

    state = { hasError: false };

    render() {
        if (this.state.hasError) {
            return (
                <Empty
                    description={this.props.errorMessage}
                    style={{ marginTop: 24 }}
                />
            );
        }
        return this.props.children;
    }
}

const ReportCoverPicker = ({ posts, hasOnlyVideos }) => {
    const { t } = useTranslation('tools');
    const { values, setFieldValue } = useFormikContext<FormValues>();

    const masonryItems = useMemo(() => {
        const seenKeys = new Set<string>();

        return posts
            .filter(
                post =>
                    post &&
                    typeof post === 'object' &&
                    typeof post.cdnMediaUrl === 'string' &&
                    post.cdnMediaUrl.trim() !== '' &&
                    (typeof post.id === 'string' ||
                        typeof post.id === 'number'),
            )
            .map(post => {
                const baseKey = String(post.id).trim();
                const safeKey = seenKeys.has(baseKey)
                    ? `${baseKey}-${Math.random().toString(36).substr(2, 6)}`
                    : baseKey;
                seenKeys.add(safeKey);

                return {
                    ...post,
                    key: safeKey,
                    isSelected: values?.postImgUrl === post.cdnMediaUrl,
                };
            })
            .filter(post => {
                return (
                    typeof post === 'object' &&
                    post !== null &&
                    typeof post.key === 'string' &&
                    post.key !== '' &&
                    typeof post.cdnMediaUrl === 'string'
                );
            });
    }, [posts, values]);

    const handleSelect = post => () => {
        setFieldValue('postImgUrl', post.cdnMediaUrl, false);
        setFieldValue('postImgHeight', post.height, false);
        setFieldValue('postImgWidth', post.width, false);
        setFieldValue('heroPostId', post.id, false);
    };

    const renderImage = ({ data }) => {
        const post = data;
        const wrapperClass = classNames('cover-post-wrapper', {
            selected: post.isSelected,
        });

        return (
            <div className={wrapperClass} onClick={handleSelect(post)}>
                <div
                    className={classNames('checkbox-container', {
                        'is-visible': post.isSelected,
                    })}
                >
                    {/* SP-8519 POC: native @app/ui Checkbox primitive (behavior/visual parity). */}
                    <Checkbox
                        aria-label="Use this post as the report cover"
                        checked={post.isSelected}
                    />
                </div>
                <img
                    className="cover-post-image"
                    src={post.cdnMediaUrl}
                    alt="post cover"
                />
            </div>
        );
    };

    return (
        <div className="cover-picker">
            <Typography.Title level={5} className="cover-picker-title">
                {t('reports.modals.coverPicker.title')}
            </Typography.Title>

            {hasOnlyVideos ? (
                <Empty
                    description={t('reports.modals.coverPicker.noVideoCover')}
                    style={{ marginTop: 24 }}
                />
            ) : (
                <>
                    <Alert
                        message={t('reports.modals.coverPicker.selectImageInfo')}
                        type="info"
                        showIcon
                        className="cover-picker-alert"
                    />
                    <div className="cover-picker-grid">
                        {masonryItems.length === 0 ? (
                            <Empty
                                description={t('reports.modals.coverPicker.noCoverImages')}
                                style={{ marginTop: 24 }}
                            />
                        ) : (
                            <MasonryErrorBoundary errorMessage={t('reports.modals.coverPicker.errorShowingImages')}>
                                <Masonry
                                    items={masonryItems}
                                    columnWidth={COLUMN_WIDTH}
                                    columnGutter={GUTTER_SIZE}
                                    render={renderImage}
                                />
                            </MasonryErrorBoundary>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ReportCoverPicker;
